import React from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { cn } from "../lib/utils"
import { LayoutProvider, useSidebar } from "../hooks/use-layout"
import { ThemeProvider, useTheme } from "../hooks/use-theme"
import { ChartsProvider } from "../components/charts"
import { Sidebar } from "./sidebar"
import { Header } from "./header"
import { Sheet, SheetContent } from "../components/ui/sheet"
import { CommandPalette } from "../components/command-palette"
import { publicAssetPath } from "helpers/publicAssetPath"
import { isOperationalKioskRoute } from "helpers/operationalRoutes"
import { recordShortcutVisit } from "helpers/quickShortcuts"
import { resolvePageVisitScope } from "helpers/pageVisits"
import { useAuth } from "slices/user/hooks"
interface AppShellProps {
 children: React.ReactNode
 menuItems: any[]
 logo?: string
 appName?: string
}

function AppShellInner({ children, menuItems, logo, appName }: AppShellProps) {
 const { t, i18n } = useTranslation()
 const { user } = useAuth()
 const isRtl = i18n.dir() === "rtl"
 const { pathname, search } = useLocation()
 const hideFooterOnOperational = isOperationalKioskRoute(pathname, search)
 const { isMobileOpen, setMobileOpen, isCollapsed } = useSidebar()
 const mobileNavSide = i18n.dir() === "rtl"? "right":"left"
const { resolvedTheme, setTheme } = useTheme()
 const previousPathnameRef = React.useRef(pathname)

 React.useEffect(() => {
  if (previousPathnameRef.current === pathname) return
  previousPathnameRef.current = pathname
  const main = document.getElementById('workspace-main-content')
  if (!main) return
  const frame = window.requestAnimationFrame(() => {
   const focused = document.activeElement
   if (focused instanceof HTMLElement && main.contains(focused)) return
   main.focus({ preventScroll: true })
  })
  return () => window.cancelAnimationFrame(frame)
 }, [pathname])

 React.useEffect(() => {
  recordShortcutVisit(pathname, search, menuItems, resolvePageVisitScope(user?.id))
 }, [menuItems, pathname, search, user?.id])

 const isDark = resolvedTheme === "dark"
const toggleTheme = () => {
 setTheme(isDark ? "light":"dark")
 }

 return (
 <div
  className="fixed inset-0 flex h-dvh min-h-0 min-w-0 max-w-full flex-col overflow-clip bg-background app-shell"
  style={{ ['--app-footer-h' as string]: hideFooterOnOperational ? '0px' : '5.5rem' }}
 >
  <a
   href="#workspace-main-content"
   className="sr-only z-[300] rounded-md bg-background px-4 py-2 text-foreground shadow-lg focus:not-sr-only focus:absolute focus:start-4 focus:top-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
  >
   {isRtl ? 'تخطي إلى المحتوى' : 'Skip to main content'}
  </a>
 <Sidebar menuItems={menuItems} logo={logo} appName={appName} />
 <CommandPalette menuItems={menuItems as any} />

 <Sheet modal open={isMobileOpen} onOpenChange={setMobileOpen}>
 <SheetContent
 side={mobileNavSide}
 className="w-sidebar border-border-subtle bg-sidebar p-0 text-sidebar-foreground shadow-modal">
 <Sidebar inDrawer menuItems={menuItems} logo={logo} appName={appName} />
 </SheetContent>
 </Sheet>

 <div
 className={cn(
"app-content flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-clip md:transition-all md:duration-300",
 isRtl
 ? cn("mr-0 md:mr-sidebar", isCollapsed && "md:mr-sidebar-collapsed")
 : cn("ml-0 md:ml-sidebar", isCollapsed && "md:ml-sidebar-collapsed"),
 )}
 >
 <Header onToggleTheme={toggleTheme} isDark={isDark} menuItems={menuItems} />
 <main
  id="workspace-main-content"
  tabIndex={-1}
  className="flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-y-auto overscroll-y-contain scroll-pb-[var(--app-footer-h,0px)] bg-background focus-visible:outline-none"
 >
 {/* Single page gutter; flex chain lets full-height routes (e.g. POS) fill without 100dvh overflow */}
 <div className="page-transition page-wrapper flex min-h-0 min-w-0 max-w-full flex-1 flex-col px-3 py-3 sm:px-4 sm:py-4 lg:px-6 lg:py-6">
 {children}
 </div>
 </main>
 <footer
 className={cn(
"app-footer pointer-events-none relative z-0 shrink-0 border-t border-border-subtle/70 bg-background py-2.5 px-3 sm:px-4 lg:px-6",
 hideFooterOnOperational &&"hidden")}
 >
 <div className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1.5">
 <img
 src={publicAssetPath("logo.png")}
 alt=""
 className="h-5 w-auto max-w-[96px] object-contain"
 />
 <span className="hidden h-3 w-px bg-border-subtle sm:block" aria-hidden />
 <p className="inline-flex flex-wrap items-center justify-center gap-1.5 text-[11px] leading-none text-muted-foreground">
 <span>{t("Developed by")}</span>
 <span
 dir="ltr"
 className="rounded-md bg-primary-soft px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-primary-soft-foreground"
 >
 madar
 </span>
 <span dir="ltr" className="text-content-caption">
 © {new Date().getFullYear()}
 </span>
 </p>
 </div>
 </footer>
 </div>
 </div>
 )
}

export function AppShell({ children, menuItems, logo, appName }: AppShellProps) {
 return (
 <ThemeProvider>
 <ChartsProvider>
 <LayoutProvider>
 <AppShellInner menuItems={menuItems} logo={logo} appName={appName}>
 {children}
 </AppShellInner>
 </LayoutProvider>
 </ChartsProvider>
 </ThemeProvider>
 )
}
